/* Live CRM (OAuth) connect panel — real production OAuth 2.0 connections.
   Additive to the sandbox Integration Center; shows credential-gated status
   honestly and launches the provider's real authorization flow. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Plug, ShieldCheck, CheckCircle2, XCircle, KeyRound, Lock, AlertTriangle } from "lucide-react";
import { DocSyncLimitationBanner } from "@/components/EnterpriseCrm";

const CAP_LABELS = { contacts: "Contacts", matters: "Matters", notes: "Notes", timeline: "Timeline", documents: "Documents", messaging: "Messaging" };
const NAMES = { clio: "Clio Manage", practicepanther: "PracticePanther", mycase: "MyCase", needles: "Needles / Neos" };
const err = (e) => formatApiErrorDetail(e.response?.data?.detail) || "Request failed";

const StatusBadge = ({ p }) => {
  if (p.connected) return <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-600 flex items-center gap-1" data-testid={`live-status-${p.provider}`}><CheckCircle2 className="w-3.5 h-3.5" /> Live connected</span>;
  const map = {
    ready: ["bg-cobalt/10 text-cobalt", "Ready to connect"],
    credential_required: ["bg-amber-50 text-amber-700", "Credential required"],
    partner_program_required: ["bg-violet-50 text-violet-700", "Partner program"],
  };
  const [cls, label] = map[p.credential_status] || ["bg-slate-100 text-slate-500", "Unavailable"];
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${cls}`} data-testid={`live-status-${p.provider}`}><Lock className="w-3.5 h-3.5" /> {label}</span>;
};

export function CrmLiveConnect() {
  const [providers, setProviders] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/crm/oauth/status").then(({ data }) => setProviders(data.providers)).catch(() => setProviders([])), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const crm = q.get("crm"), live = q.get("live");
    if (crm && live) {
      if (live === "connected") toast.success(`${NAMES[crm] || crm} connected live`);
      else if (live === "error") toast.error(`${NAMES[crm] || crm} authorization failed`);
      else if (live === "declined") toast.message(`${NAMES[crm] || crm} authorization was cancelled`);
      window.history.replaceState({}, "", "/integrations");
      load();
    }
  }, [load]);

  const connect = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.get(`/crm/oauth/${id}/authorize`);
      window.location.href = data.auth_url;
    } catch (e) { toast.error(err(e)); setBusy(null); }
  };
  const disconnect = async (id) => {
    setBusy(id);
    try { await api.delete(`/crm/oauth/${id}`); toast.success(`${NAMES[id] || id} disconnected`); load(); }
    catch (e) { toast.error(err(e)); } finally { setBusy(null); }
  };
  const toggleSync = async (id, enabled) => {
    try { await api.post(`/crm/oauth/${id}/keep-sync`, { enabled }); toast.success(`Keep-in-Sync ${enabled ? "enabled" : "paused"}`); load(); }
    catch (e) { toast.error(err(e)); }
  };

  if (!providers) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;

  const availableNow = providers.filter((p) => ["clio", "practicepanther", "mycase"].includes(p.provider));
  const comingPriority = providers.filter((p) => p.provider === "needles");

  const Card = ({ p, pending }) => (
    <div className="bg-white rounded-3xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`live-card-${p.provider}`}>
      <div className="flex items-center justify-between">
        <div className="font-heading font-bold text-navy">{NAMES[p.provider] || p.provider}</div>
        {pending ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 flex items-center gap-1" data-testid={`live-status-${p.provider}`}><Lock className="w-3.5 h-3.5" /> Partner Integration Pending</span> : <StatusBadge p={p} />}
      </div>
      <div className="mt-2 text-xs text-slate-500 space-y-1">
        <div data-testid={`live-caps-${p.provider}`}>{Object.keys(CAP_LABELS).filter((k) => p.capabilities?.[k]).map((k) => CAP_LABELS[k]).join(" · ") || "Capabilities activate with partner credentials"}</div>
        {p.note && <div className="text-amber-600">{p.note}</div>}
      </div>
      {p.connected && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`live-chips-${p.provider}`}>
          <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Connected</span>
          {p.keep_synced && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Keep-in-Sync Active</span>}
          {p.capabilities?.documents === false && (
            <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 flex items-center gap-1" data-testid={`live-doc-unsupported-${p.provider}`}>
              <AlertTriangle className="w-3 h-3" /> Document Uploads Not Supported by CRM
            </span>
          )}
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2 items-center">
        {p.connected ? (
          <>
            <button onClick={() => disconnect(p.provider)} disabled={busy === p.provider} data-testid={`live-disconnect-${p.provider}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 transition-all">{busy === p.provider ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Disconnect</button>
            <label className="flex items-center gap-2 text-xs text-slate-600 ml-1" data-testid={`live-keepsync-${p.provider}`}>
              <input type="checkbox" checked={!!p.keep_synced} onChange={(e) => toggleSync(p.provider, e.target.checked)} data-testid={`live-keepsync-checkbox-${p.provider}`} /> Keep in Sync
            </label>
          </>
        ) : pending ? (
          <a href={p.docs?.split(" ")[0]} target="_blank" rel="noreferrer" data-testid={`live-docs-${p.provider}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-all"><KeyRound className="w-4 h-4" /> Partner program</a>
        ) : p.credential_status === "ready" ? (
          <button onClick={() => connect(p.provider)} disabled={busy === p.provider} data-testid={`live-connect-${p.provider}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover transition-all active:scale-95">{busy === p.provider ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Connect with OAuth</button>
        ) : (
          <a href={p.docs?.split(" ")[0]} target="_blank" rel="noreferrer" data-testid={`live-docs-${p.provider}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-all"><KeyRound className="w-4 h-4" /> Setup required</a>
        )}
      </div>
      {!p.connected && (
        <p className="mt-2 text-[11px] text-slate-400" data-testid={`live-gate-note-${p.provider}`}>
          {pending ? "No public API — activates once Assembly grants Integration Partner Program credentials. Already scaffolded and ready."
            : p.credential_status === "ready" ? "Production OAuth ready — connect your firm's account."
            : "Register a developer app and add the API credentials to enable live connection."}
        </p>
      )}
    </div>
  );

  return (
    <div className="mt-10" data-testid="crm-live-connect">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-cobalt" />
        <h2 className="font-heading text-lg font-bold text-navy">Available Now — Live CRM Connections (OAuth)</h2>
      </div>
      <p className="mt-1 text-sm text-slate-600 max-w-2xl">Connect your real CRM account with production OAuth 2.0 to import claimed cases and keep them in sync automatically.</p>
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {availableNow.map((p) => <Card key={p.provider} p={p} />)}
      </div>
      {availableNow.some((p) => p.connected && p.capabilities?.documents === false) && (
        <DocSyncLimitationBanner className="mt-4"
          providers={availableNow.filter((p) => p.connected && p.capabilities?.documents === false).map((p) => NAMES[p.provider] || p.provider)} />
      )}
      {comingPriority.length > 0 && (
        <div className="mt-8">
          <h3 className="font-heading text-base font-bold text-navy" data-testid="live-coming-priority">Priority (Partner Integration Pending)</h3>
          <p className="text-xs text-slate-500 mb-3">Next on our roadmap — scaffolded and ready to activate once partner credentials are provisioned.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {comingPriority.map((p) => <Card key={p.provider} p={p} pending />)}
          </div>
        </div>
      )}
    </div>
  );
}
